import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router'
import {
	PlusCircle,
	Settings,
	ShoppingCart,
	Store,
	Users,
	WalletCards,
} from 'lucide-react'
import type { ReactNode } from 'react'
import { useAdminTrpc } from 'shared/api'
import { Card } from 'shared/components/ui/card'
import { useTranslate } from 'shared/lib/i18n'
import { useAuthStore } from 'shared/store/auth'
import {
	getDashboardCardKeys,
	type DashboardCardKey,
} from '../lib/cards'

export function AdminDashboard() {
	const trpc = useAdminTrpc()
	const navigate = useNavigate()
	const user = useAuthStore((state) => state.user)
	const { prefixT } = useTranslate('pages.adminDashboard')
	const { data, isError } = useQuery(trpc.dashboard.Summary.queryOptions())
	const n = (v: number | undefined) => (isError || v === undefined ? '—' : v)

	const buckets: Record<
		DashboardCardKey,
		{
			title: string
			count: string | number
			icon: ReactNode
			to: string
		}
	> = {
		createLot: {
			title: prefixT('createLot'),
			count: '',
			icon: <PlusCircle className='size-5' />,
			to: '/admin/lots/new',
		},
		pendingTopups: {
			title: prefixT('pendingTopups'),
			count: n(data?.pendingTopups),
			icon: <WalletCards className='size-5' />,
			to: '/admin/topups',
		},
		readyLots: {
			title: prefixT('readyLots'),
			count: n(data?.lots?.ready),
			icon: <ShoppingCart className='size-5' />,
			to: '/admin/lots?f=ready',
		},
		clients: {
			title: prefixT('clients'),
			count: '',
			icon: <Users className='size-5' />,
			to: '/admin/clients',
		},
		storefront: {
			title: prefixT('storefront'),
			count: '',
			icon: <Store className='size-5' />,
			to: '/',
		},
		settings: {
			title: prefixT('settings'),
			count: '',
			icon: <Settings className='size-5' />,
			to: '/admin/settings',
		},
	}

	return (
		<div className='flex flex-col gap-3 p-4'>
			<h1 className='text-lg font-semibold'>{prefixT('title')}</h1>
			{getDashboardCardKeys(user).map((key) => {
				const b = buckets[key]
				return (
					<Card
						key={b.to}
						className='flex cursor-pointer flex-row items-center justify-between p-4'
						onClick={() => navigate(b.to)}
					>
						<div className='flex items-center gap-3'>
							{b.icon}
							<span>{b.title}</span>
						</div>
						<span className='text-xl font-bold'>{b.count}</span>
					</Card>
				)
			})}
		</div>
	)
}
